/**
 * Качество и ОТ на обзоре карточки проекта (поток 7): открытые и просроченные замечания ОТК, ОТ, ПБ, экологии и
 * приёмки по чек-листам, со ссылками в раздел. Видно сотрудникам с правом «Видеть качество и ОТ» на проекте.
 */
import { can, type ProjectDetail, type QualityProjectSummary, type SafetySummary } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api } from '../../lib/api';
import { useMe } from '../../lib/session';
import { ErrorBox, Spinner } from '../../ui/feedback';
import { Panel } from '../../ui/layout';

export function QualityPanel({
  project,
  target,
}: {
  project: ProjectDetail;
  target: {
    legalEntityId: string;
    directionId: string | null;
    projectId: string;
    projectPath: Array<string | null>;
  };
}) {
  const me = useMe();
  const sees = Boolean(me.tenant?.staff) && can(me.access, 'qhse.view', target);
  const q = useQuery({
    queryKey: ['qa-summary', project.id],
    queryFn: () => api<QualityProjectSummary>('/quality/summary', { query: { projectId: project.id } }),
    enabled: sees,
  });
  const safety = useQuery({
    queryKey: ['qa-safety-summary', project.id],
    queryFn: () => api<SafetySummary>('/quality/safety-summary', { query: { projectId: project.id } }),
    enabled: sees,
  });
  if (!sees) return null;
  const cell = (label: string, value: number, warn: number, search: Record<string, string>) => (
    <Link
      to="/qhse"
      search={{ project: project.id, ...search }}
      className="flex min-h-11 flex-col rounded-md border border-line px-4 py-3 hover:border-ink-3 hover:bg-surface-2"
    >
      <span className="text-[13px] text-ink-2">{label}</span>
      <span className="num text-[22px] font-semibold text-ink">
        {value}
        {warn ? <span className="ml-2 text-[13px] font-medium text-danger">просрочено {warn}</span> : null}
      </span>
    </Link>
  );
  return (
    <Panel title="Качество и ОТ" meta={`проект ${project.code}`}>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {cell('Открытые замечания ОТК, ОТ, ПБ', q.data.openRemarks, q.data.overdueRemarks, {})}
          {cell('Приёмки по чек-листам', q.data.openInspections, q.data.overdueInspections, {
            tab: 'inspections',
          })}
          {safety.data ? (
            <>
              {cell('Действующие наряды-допуски', safety.data.activePermits, safety.data.expiredPermits, {
                tab: 'permits',
              })}
              {safety.data.suspendedPermits
                ? cell('Наряды: работы остановлены', safety.data.suspendedPermits, 0, {
                    tab: 'permits',
                  })
                : null}
              {cell(
                'Допуски работников: истекают за 30 дней',
                safety.data.credentialsExpiring,
                safety.data.credentialsExpired,
                { tab: 'credentials' },
              )}
              {cell('Инструктажи: сбор подписей', safety.data.briefingsOpen, safety.data.repeatOverdue, {
                tab: 'briefings',
              })}
              {cell(
                'Наблюдения за безопасностью: новые',
                safety.data.observationsNew,
                safety.data.observationsOverdue,
                {
                  tab: 'observations',
                },
              )}
            </>
          ) : null}
        </div>
      )}
    </Panel>
  );
}
